/**
 * Color Siblings Service & UI
 *
 * Fetches sibling products (same style family) via Storefront API
 * with automatic fallback to predictive search.
 *
 * Exposes a public API on window.ColorSiblings so other components
 * (e.g. quick-add-custom) can reuse the same cache and fetch logic
 * without duplicating requests.
 *
 * Public API:
 *   window.ColorSiblings.fetch(styleRef)  -> Promise<Product[]|null>
 *   window.ColorSiblings.getColorName(product) -> string
 */
(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Session storage cache (bounded LRU)
  // ---------------------------------------------------------------------------

  // v3: entries cached under v2 may contain sold-out siblings.
  var CACHE_PREFIX = "siblings_v3_";
  var MAX_CACHE_ENTRIES = 50;
  var cachedKeys = [];

  function cacheKey(styleRef) {
    return CACHE_PREFIX + styleRef;
  }

  function getFromCache(styleRef) {
    try {
      var raw = sessionStorage.getItem(cacheKey(styleRef));
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return null;
  }

  function saveToCache(styleRef, products) {
    try {
      pruneCache();
      sessionStorage.setItem(cacheKey(styleRef), JSON.stringify(products));
      cachedKeys.push(cacheKey(styleRef));
    } catch (e) {}
  }

  /** Remove oldest entries when cache exceeds MAX_CACHE_ENTRIES */
  function pruneCache() {
    try {
      while (cachedKeys.length >= MAX_CACHE_ENTRIES) {
        sessionStorage.removeItem(cachedKeys.shift());
      }
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // In-flight request deduplication
  // ---------------------------------------------------------------------------

  var pendingFetches = {};

  // ---------------------------------------------------------------------------
  // Filter helpers
  // ---------------------------------------------------------------------------

  function filterSiblings(products, styleRef) {
    var erpPrefix = styleRef + "-";
    var styleRefLower = styleRef.toLowerCase();
    return products.filter(function (p) {
      if (p.tags && p.tags.length) {
        return p.tags.some(function (t) {
          return t.indexOf(erpPrefix) === 0;
        });
      }
      return p.handle.indexOf(styleRefLower) !== -1;
    });
  }

  // ---------------------------------------------------------------------------
  // Storefront API fetch
  // ---------------------------------------------------------------------------

  var USE_STOREFRONT_API = !!(
    window.STOREFRONT_ACCESS_TOKEN &&
    window.STOREFRONT_ACCESS_TOKEN.length > 10 &&
    window.STOREFRONT_DOMAIN
  );

  /**
   * Normalize a Storefront API product node to a shape compatible
   * with predictive search results (featured_image.url, variants[]).
   * Adds colorHex and colorName from optionValues swatch data.
   */
  function normalizeFromStorefront(node) {
    var colorHex = null;
    var colorName = null;

    if (node.options) {
      for (var i = 0; i < node.options.length; i++) {
        var opt = node.options[i];
        var nameLower = opt.name.toLowerCase();
        if (nameLower === "color" || nameLower === "colour") {
          if (opt.optionValues && opt.optionValues.length > 0) {
            colorName = opt.optionValues[0].name;
            if (
              opt.optionValues[0].swatch &&
              opt.optionValues[0].swatch.color
            ) {
              colorHex = opt.optionValues[0].swatch.color;
            }
          }
          break;
        }
      }
    }

    return {
      handle: node.handle,
      title: node.title,
      tags: node.tags || [],
      featured_image: (function () {
        var edges = node.images && node.images.edges;
        var img = (edges && edges[1]) || (edges && edges[0]);
        return img ? { url: img.node.url } : null;
      })(),
      variants:
        node.variants && node.variants.edges
          ? node.variants.edges.map(function (e) {
              return { title: e.node.title };
            })
          : [],
      colorHex: colorHex,
      colorName: colorName,
      available: node.availableForSale !== false,
    };
  }

  function fetchViaSfAPI(styleRef) {
    var query =
      '{ products(first: 20, query: "' +
      styleRef +
      '") {' +
      "  edges { node {" +
      "    handle title tags availableForSale" +
      "    images(first: 2) { edges { node { url } } }" +
      "    options { name optionValues { name swatch { color } } }" +
      "    variants(first: 1) { edges { node { title } } }" +
      "  } }" +
      "} }";

    return fetch(
      "https://" + window.STOREFRONT_DOMAIN + "/api/2024-07/graphql.json",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": window.STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({ query: query }),
      },
    )
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (json) {
        if (json.errors) throw new Error(json.errors[0].message);

        var edges =
          (json.data && json.data.products && json.data.products.edges) || [];
        return filterSiblings(
          edges.map(function (e) {
            return normalizeFromStorefront(e.node);
          }),
          styleRef,
        );
      });
  }

  // ---------------------------------------------------------------------------
  // Predictive search fetch (fallback)
  // ---------------------------------------------------------------------------

  function fetchViaPredictive(styleRef) {
    var params = [
      "q=" + encodeURIComponent(styleRef),
      "resources[type]=product",
      "resources[limit]=20",
      "resources[options][fields]=tag",
      // Predictive search does not return `available`, so the render-time filter
      // cannot drop sold-out siblings. Exclude them at the source instead.
      "resources[options][unavailable_products]=hide",
    ].join("&");

    return fetch("/search/suggest.json?" + params)
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (json) {
        var results =
          (json.resources &&
            json.resources.results &&
            json.resources.results.products) ||
          [];
        return filterSiblings(results, styleRef);
      });
  }

  // ---------------------------------------------------------------------------
  // Main fetch — Storefront API with automatic fallback to predictive search
  // ---------------------------------------------------------------------------

  function fetchSiblings(styleRef) {
    var cached = getFromCache(styleRef);
    if (cached) return Promise.resolve(cached);
    if (pendingFetches[styleRef]) return pendingFetches[styleRef];

    var primaryFetch = USE_STOREFRONT_API
      ? fetchViaSfAPI(styleRef)
      : fetchViaPredictive(styleRef);

    var promise = primaryFetch
      .catch(function () {
        if (USE_STOREFRONT_API) return fetchViaPredictive(styleRef);
        return null;
      })
      .then(function (products) {
        if (products) saveToCache(styleRef, products);
        delete pendingFetches[styleRef];
        return products;
      })
      .catch(function () {
        delete pendingFetches[styleRef];
        return null;
      });

    pendingFetches[styleRef] = promise;
    return promise;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the color name for a product.
   * Storefront API products use the normalized colorName field.
   * Predictive search products parse the first variant title.
   */
  function getColorName(product) {
    if (product.colorName) return product.colorName;
    if (product.variants && product.variants.length > 0) {
      var variantTitle = product.variants[0].title;
      if (variantTitle && variantTitle !== "Default Title") {
        var parts = variantTitle.split(" / ");
        return parts[parts.length - 1];
      }
    }
    return product.title;
  }

  // ---------------------------------------------------------------------------
  // Render — uses DocumentFragment to batch DOM operations
  // ---------------------------------------------------------------------------

  function renderSwatches(container, siblings, currentHandle) {
    container
      .querySelectorAll(".color-siblings__skeleton")
      .forEach(function (el) {
        el.remove();
      });

    if (!siblings || siblings.length === 0) return;

    var swatchStyle = container.dataset.swatchStyle || "image";
    var otherSiblings = siblings.filter(function (s) {
      return s.handle !== currentHandle && s.available !== false;
    });

    if (otherSiblings.length === 0) return;

    var fragment = document.createDocumentFragment();

    otherSiblings.forEach(function (sibling) {
      var colorName = getColorName(sibling);
      var link = document.createElement("a");
      link.href = "/products/" + sibling.handle;
      link.className = "color-siblings__swatch";
      link.setAttribute("aria-label", colorName);
      link.setAttribute("title", colorName);
      link.dataset.handle = sibling.handle;

      if (swatchStyle === "color") {
        var hex = sibling.colorHex || null;
        var dot = document.createElement("span");
        dot.className = hex
          ? "color-siblings__swatch-dot"
          : "color-siblings__swatch-fallback";
        if (hex) dot.style.backgroundColor = hex;
        link.appendChild(dot);
      } else {
        var imgObj = sibling.featured_image || sibling.image;
        var imgUrl = imgObj && imgObj.url;
        if (imgUrl) {
          var media = document.createElement("span");
          media.className = "color-siblings__swatch-media";
          var img = document.createElement("img");
          img.src = imgUrl + (imgUrl.indexOf("?") !== -1 ? "&" : "?") + "width=88";
          img.alt = colorName;
          img.loading = "lazy";
          img.decoding = "async";
          img.className = "color-siblings__swatch-img";
          media.appendChild(img);
          link.appendChild(media);
        } else {
          var fallback = document.createElement("span");
          fallback.className = "color-siblings__swatch-fallback";
          link.appendChild(fallback);
        }
      }

      fragment.appendChild(link);
    });

    container.appendChild(fragment);
  }

  function showSkeletons(container) {
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < 3; i++) {
      var skeleton = document.createElement("span");
      skeleton.className = "color-siblings__swatch color-siblings__skeleton";
      skeleton.setAttribute("aria-hidden", "true");
      var inner = document.createElement("span");
      inner.className =
        "color-siblings__swatch-fallback color-siblings__swatch-fallback--skeleton";
      skeleton.appendChild(inner);
      fragment.appendChild(skeleton);
    }
    container.appendChild(fragment);
  }

  function getBreadcrumbSectionId() {
    var el = document.querySelector(".breadcrumb-section[data-section-id]");
    return el ? el.dataset.sectionId : null;
  }

  function rerunScripts(container) {
    container.querySelectorAll("script").forEach(function (oldScript) {
      var newScript = document.createElement("script");
      Array.from(oldScript.attributes).forEach(function (attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  function findCardParent(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var tag = node.tagName;
      var cls = typeof node.className === "string" ? node.className : "";
      if (
        tag === "LI" ||
        tag === "ARTICLE" ||
        tag === "PRODUCT-CARD" ||
        cls.indexOf("product-card") !== -1 ||
        cls.indexOf("product-item") !== -1 ||
        cls.indexOf("grid-item") !== -1 ||
        cls.indexOf("card--product") !== -1
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return el.parentElement;
  }

  // ---------------------------------------------------------------------------
  // PDP initialization — with hover prefetch for instant section swaps
  // ---------------------------------------------------------------------------

  /** In-memory cache for prefetched section HTML keyed by product handle */
  var sectionCache = {};

  /**
   * Prefetch section HTML for a sibling product.
   * Called on mouseenter so data is ready before the user clicks.
   * Returns a promise that resolves to the parsed JSON.
   */
  function prefetchSection(handle, sectionsParam) {
    if (sectionCache[handle]) return sectionCache[handle];

    var url = "/products/" + handle + "?sections=" + sectionsParam;
    var promise = fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .catch(function () {
        delete sectionCache[handle];
        return null;
      });

    sectionCache[handle] = promise;
    return promise;
  }

  function initPDP() {
    var container = document.querySelector('[data-style-ref][data-mode="pdp"]');
    if (!container) return;

    var styleRef = container.dataset.styleRef;
    var currentHandle = container.dataset.currentHandle;
    var sectionId = container.dataset.sectionId;

    showSkeletons(container);

    fetchSiblings(styleRef).then(function (siblings) {
      if (!siblings) {
        container
          .querySelectorAll(".color-siblings__skeleton")
          .forEach(function (el) {
            el.remove();
          });
        return;
      }
      renderSwatches(container, siblings, currentHandle);
    });

    // Build sections param once for reuse in prefetch + click
    var breadcrumbSectionId = getBreadcrumbSectionId();
    var sectionsParam = breadcrumbSectionId
      ? sectionId + "," + breadcrumbSectionId
      : sectionId;

    // Prefetch section HTML on hover so click feels instant
    container.addEventListener("mouseenter", function (e) {
      var swatch = e.target.closest(".color-siblings__swatch");
      if (!swatch) return;
      if (swatch.getAttribute("aria-current") === "true") return;
      if (!sectionId) return;

      var handle = swatch.dataset.handle;
      if (handle) prefetchSection(handle, sectionsParam);
    }, true);

    // Apply prefetched (or fresh) section data on click
    container.addEventListener("click", function (e) {
      var swatch = e.target.closest(".color-siblings__swatch");
      if (!swatch) return;
      // El swatch activo es un <a> al propio producto: sin esto, tocar el color que ya se
      // esta viendo recarga la PDP entera para volver al mismo lugar.
      if (swatch.getAttribute("aria-current") === "true") {
        e.preventDefault();
        return;
      }
      if (!sectionId) return;

      e.preventDefault();

      var handle = swatch.dataset.handle;

      container.classList.add("color-siblings--loading");

      // Use prefetched data if available, otherwise fetch now
      var dataPromise = prefetchSection(handle, sectionsParam);

      dataPromise
        .then(function (data) {
          if (!data) {
            window.location.href = "/products/" + handle;
            return;
          }
          var wrapper = document.getElementById("shopify-section-" + sectionId);
          if (wrapper && data[sectionId]) {
            wrapper.innerHTML = data[sectionId];
            rerunScripts(wrapper);
            history.pushState({}, "", "/products/" + handle);
            var titleEl = wrapper.querySelector("[data-product-title]");
            if (titleEl) document.title = titleEl.dataset.productTitle;
            if (breadcrumbSectionId && data[breadcrumbSectionId]) {
              var breadcrumbWrapper = document.getElementById(
                "shopify-section-" + breadcrumbSectionId,
              );
              if (breadcrumbWrapper) {
                breadcrumbWrapper.innerHTML = data[breadcrumbSectionId];
                breadcrumbWrapper.dispatchEvent(
                  new CustomEvent("shopify:section:load", { bubbles: true }),
                );
              }
            }
            initPDP();
          } else {
            window.location.href = "/products/" + handle;
          }
        })
        .catch(function () {
          window.location.href = "/products/" + handle;
        });
    });
  }

  // ---------------------------------------------------------------------------
  // PLP initialization — debounced mouseenter (250ms)
  // ---------------------------------------------------------------------------

  var CARD_SELECTOR = '[data-style-ref][data-mode="card"]';

  function initPLP() {
    var inFlight =
      typeof WeakSet === "function" ? new WeakSet() : { has: function () { return false; }, add: function () {}, delete: function () {} };

    // Shared by both triggers so a container can never be fetched twice, and so a failed request
    // still leaves the container retryable — the original only marked `loaded` after a render.
    function loadSiblings(container) {
      if (container.dataset.loaded || inFlight.has(container)) return;
      inFlight.add(container);

      var styleRef = container.dataset.styleRef;
      var currentHandle = container.dataset.currentHandle;

      fetchSiblings(styleRef)
        .then(function (siblings) {
          inFlight.delete(container);
          if (!siblings) return;
          renderSwatches(container, siblings, currentHandle);
          container.dataset.loaded = "true";
        })
        .catch(function () {
          inFlight.delete(container);
        });
    }

    // ------------------------------------------------------------------
    // Touch devices: visibility is the only intent signal there is
    // ------------------------------------------------------------------
    //
    // `mouseenter` was the sole trigger here, so on a phone the swatches never rendered at all:
    // scrolling a collection fires no pointer events, and the container — which carries no
    // mobile-specific CSS and stays `display: flex` — simply sat empty. The PDP was unaffected
    // because `initPDP` loads immediately, which is why the bug read as "PLP only".
    //
    // Hover-capable devices keep the debounced hover exactly as it was, so the desktop behaviour
    // and its request profile do not change. Where the device cannot hover, the card entering the
    // viewport takes its place, which still means paying only for cards the shopper actually
    // reaches rather than for the whole grid on load.
    var observer = null;

    function observeCards() {
      if (observer || typeof IntersectionObserver !== "function") return;

      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            loadSiblings(entry.target);
          });
        },
        { rootMargin: "200px 0px" },
      );

      bound.forEach(function (container) {
        if (container.dataset.loaded) return;
        observer.observe(container);
      });
    }

    // Every container this run has already wired, so a card is never bound twice and so
    // `observeCards` can pick up whatever was bound before the observer existed.
    var bound = [];
    var seen =
      typeof WeakSet === "function"
        ? new WeakSet()
        : { has: function () { return false; }, add: function () {} };

    function bindContainer(container) {
      if (seen.has(container)) return;
      seen.add(container);
      bound.push(container);

      var card = findCardParent(container);
      var debounceTimer = null;

      card.addEventListener("mouseenter", function () {
        if (container.dataset.loaded) return;

        debounceTimer = setTimeout(function () {
          loadSiblings(container);
        }, 250);
      });

      card.addEventListener("mouseleave", function () {
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
      });

      if (observer && !container.dataset.loaded) observer.observe(container);
    }

    function bindAllIn(root) {
      if (!root || root.nodeType !== 1) return;
      if (root.matches && root.matches(CARD_SELECTOR)) bindContainer(root);
      if (!root.querySelectorAll) return;
      Array.prototype.forEach.call(
        root.querySelectorAll(CARD_SELECTOR),
        bindContainer,
      );
    }

    // ------------------------------------------------------------------
    // Mobile: cambiar de color repinta la card en vez de navegar
    // ------------------------------------------------------------------
    //
    // En la card conviven dos pickers de color. En desktop se usa el del overlay de quick-add,
    // que repinta la card sin salir de la grilla. En mobile ese overlay esta oculto por CSS
    // (`snippets/quick-add-custom.liquid`), asi que el unico visible es este, y sus swatches son
    // `<a href>` que se llevan al usuario a la PDP: para comparar dos colores hay que entrar y
    // volver. Este listener le da a mobile el mismo comportamiento que ya tiene desktop.
    //
    // La condicion no es un breakpoint repetido sino si el picker del overlay esta visible: el
    // CSS es el que decide cual manda, y duplicar los 749px aca los dejaria capaces de
    // desincronizarse. `offsetParent` fuerza layout, pero corre una vez por tap, no en scroll.
    //
    // El `<a href>` se conserva intacto: si el componente no esta (JS que no cargo, card sin
    // quick-add) no se previene nada y el tap navega igual que hoy.
    document.addEventListener("click", function (e) {
      var swatch = e.target.closest && e.target.closest(".color-siblings__swatch");
      if (!swatch) return;

      var container = swatch.closest(CARD_SELECTOR);
      if (!container) return;

      // `findCardParent` no sirve aca: corta en el primer ancestro cuya clase CONTENGA
      // "product-card", y el padre inmediato del container es `product-card-simple__swatches`,
      // que la contiene. Devolvia ese div, que no incluye al componente — los swatches viven
      // fuera del <a> de la card y el quick-add adentro, asi que el ancestro comun es la card
      // entera. Se busca por elemento, que no depende de como se llamen las clases.
      var card = container.closest("product-card, li, article");
      var component = card && card.querySelector("quick-add-custom-component");
      if (!component || typeof component.selectSibling !== "function") return;

      var overlayColors = component.querySelector("[data-siblings-colors]");
      if (overlayColors && overlayColors.offsetParent !== null) return;

      var handle = swatch.dataset.handle;
      if (!handle) return;

      e.preventDefault();

      // El swatch activo se marca aca porque este picker lleva su propio estado: el componente
      // solo repinta la card y no conoce este DOM.
      Array.prototype.forEach.call(
        container.querySelectorAll(".color-siblings__swatch"),
        function (other) {
          other.removeAttribute("aria-current");
          other.classList.remove("color-siblings__swatch--active");
        },
      );
      swatch.setAttribute("aria-current", "true");
      swatch.classList.add("color-siblings__swatch--active");

      // Volver al color con el que la card se renderizo tiene su propio camino: restaura el
      // markup original sin pedir nada a la red. El swatch activo es un <a> server-side al
      // propio producto y tambien entra aca, asi que sin esta rama volver costaria un fetch
      // de ida y vuelta.
      if (handle === container.dataset.currentHandle) {
        component.restoreOriginal();
        return;
      }

      component.selectSibling(handle);
    });

    bindAllIn(document.body);

    // ------------------------------------------------------------------
    // Cards that arrive later
    // ------------------------------------------------------------------
    //
    // The collection grid pages in as the shopper scrolls — measured on the preview, 24 cards
    // become 48 and then 72. Binding only what existed at DOMContentLoaded left every one of those
    // later cards without swatches: on a phone the count of loaded containers froze at 24 while the
    // grid kept growing. The hover path had the same hole, it was simply invisible because hover
    // never reached those cards on desktop either without a deliberate mouse move.
    if (typeof MutationObserver === "function") {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          Array.prototype.forEach.call(mutation.addedNodes, bindAllIn);
        });
      }).observe(document.body, { childList: true, subtree: true });
    }

    if (typeof window.matchMedia !== "function") return;

    var hoverless = window.matchMedia("(hover: none)");
    if (hoverless.matches) observeCards();

    // A tablet that loses its mouse, or a device that reports hover late, still gets the swatches.
    if (typeof hoverless.addEventListener === "function") {
      hoverless.addEventListener("change", function (event) {
        if (event.matches) observeCards();
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Public API — consumed by quick-add-custom and any future component
  // ---------------------------------------------------------------------------

  window.ColorSiblings = {
    fetch: fetchSiblings,
    getColorName: getColorName,
  };

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initPDP();
      initPLP();
    });
  } else {
    initPDP();
    initPLP();
  }
})();
